import { first } from 'rxjs';
import { NgIf } from '@angular/common';
import { Params, Router } from '@angular/router';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { NotificationComponent } from '@modules/notification-queue/abstract/notification.component';
import { NotificationQueueHttpService } from '@modules/notification-queue/services/notification-queue-http.service';
import { InternalTripNotificationSignalRInterface } from '@interfaces/singnal-r/internal-trip-notification-signal-r.interface';
import { DsNotificationComponent } from '@design-system/ds-notification/component/ds-notification.component';
import { SNOOZE_TIME_RANGE } from '@modules/notification-queue/properties/notification-queue.properties';
import { NotificationService } from '@notification/service';

@Component({
  selector: 'app-internal-trip-notification',
  standalone: true,
  templateUrl: './internal-trip-notification.component.html',
  styleUrls: ['./internal-trip-notification.component.scss'],
  imports: [NgIf, DsNotificationComponent],
  providers: [NotificationQueueHttpService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InternalTripNotificationComponent extends NotificationComponent {
  @Input() override notification: InternalTripNotificationSignalRInterface | null = null;

  /**
   * Available snooze options.
   */
  snoozeTimeRange = SNOOZE_TIME_RANGE;

  constructor(
    router: Router,
    private readonly notificationQueueHttpService: NotificationQueueHttpService,
    private readonly toastNotificationService: NotificationService,
  ) {
    super(router);
  }

  /**
   * Navigates to the service-request list page with specified query param
   */
  override async onViewServiceRequest(): Promise<void> {
    const queryParams: Params = { tripId: this.notification.trip };

    return super.onViewServiceRequest(null, this.notification.notificationId, '/service-request', queryParams);
  }

  /**
   * Send API call to dismiss internal trip notification and close the notification window
   */
  override closeNotification(): void {
    const notificationId = this.notification.notificationId;

    this.notificationQueueHttpService.dismissNotification(notificationId).pipe(first()).subscribe();

    super.closeNotification(notificationId);
  }

  /**
   * Sends API call to snooze internal trip notification and closes the notification.
   * @param minutes - selected snooze duration in minutes
   */
  onSnoozeSelect(minutes: number): void {
    const notificationId = this.notification.notificationId;
    this.notificationQueueHttpService
      .snoozeNotification(notificationId, minutes)
      .pipe(first())
      .subscribe(() => {
        this.toastNotificationService.success(`Snoozed for ${minutes} minutes`, 'Success');
      });

    super.closeNotification(notificationId);
  }
}
